// src/components/media/MediaPreview.tsx — the preview: a photo full size with arrows (← → on the keyboard), a PDF in
// the browser's viewer, a video with its controls. Beside it, the file's details; with media.update the label, the
// caption and the file name are edited here.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X, Download, Loader2, Star } from 'lucide-react';
import { Button, Field, Input, Textarea } from '../ui';
import { LabelPicker } from './LabelPicker';
import { errorText } from './toast';
import { downloadItem, formatBytes, mediaApi, useMediaBlob, useMediaFile, type MediaItem } from '../../lib/media';

function Stage({ item }: { item: MediaItem }) {
  const image = useMediaBlob(item.kind === 'IMAGE' ? item.url : null);
  const file = useMediaFile(item.kind !== 'IMAGE' ? item.url : null);
  const src = item.kind === 'IMAGE' ? image.src : file.src;
  const failed = item.kind === 'IMAGE' ? image.error : file.error;
  if (failed) return <p className="text-[13px] text-white/70">This file could not be loaded.</p>;
  if (!src) return <Loader2 className="h-6 w-6 animate-spin text-white/60" aria-label="Loading" />;
  if (item.kind === 'IMAGE') return <img src={src} alt={item.displayLabel ?? item.fileName} className="max-h-full max-w-full rounded-md object-contain shadow-pop" />;
  if (item.kind === 'VIDEO') return <video src={src} controls autoPlay={false} className="max-h-full max-w-full rounded-md bg-black shadow-pop" />;
  return <iframe src={src} title={item.fileName} className="h-full w-full rounded-md bg-white" />;
}

function Details({ item, canUpdate, onSaved }: { item: MediaItem; canUpdate: boolean; onSaved: (i: MediaItem) => void }) {
  const [label, setLabel] = useState(item.label ?? '');
  const [caption, setCaption] = useState(item.caption ?? '');
  const [name, setName] = useState(item.fileName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const dirty = label !== (item.label ?? '') || caption.trim() !== (item.caption ?? '') || name.trim() !== item.fileName;
  const save = async () => {
    setBusy(true); setError(null); setSaved(false);
    try {
      const body: { label?: string | null; caption?: string | null; fileName?: string } = {};
      if (label !== (item.label ?? '')) body.label = label || null;
      if (caption.trim() !== (item.caption ?? '')) body.caption = caption.trim() || null;
      if (name.trim() && name.trim() !== item.fileName) body.fileName = name.trim();
      onSaved(await mediaApi.updateItem(item.id, body));
      setSaved(true);
    } catch (e) { setError(errorText(e)); }
    setBusy(false);
  };
  const dims = item.width && item.height ? `${item.width} × ${item.height}` : null;
  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        <div>
          <p className="eyebrow mb-1">{item.kind === 'IMAGE' ? 'Photo' : item.kind === 'PDF' ? 'PDF' : 'Video'}</p>
          <p className="break-words text-[14px] font-semibold text-ink-900">{item.displayLabel ?? item.fileName}</p>
          <p className="mt-0.5 text-[12px] text-ink-500">{[formatBytes(item.sizeBytes), dims].filter(Boolean).join(' · ')}</p>
          {item.isCover && <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-bronze-50 px-2 py-0.5 text-[11px] font-semibold text-bronze-700"><Star className="h-3 w-3" />Cover — the first photo the website shows</p>}
        </div>
        {canUpdate ? <>
          <Field label="Label" hint={item.labelIndex ? `Shown as “${item.displayLabel}”: there is more than one ${item.label} in this folder.` : 'What the photo shows; printed under it on the website.'}>
            <LabelPicker value={label} onChange={setLabel} />
          </Field>
          <Field label="Caption"><Textarea rows={3} maxLength={300} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Main bedroom with fitted wardrobes" className="w-full" /></Field>
          <Field label="File name"><Input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} className="w-full" /></Field>
          {error && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">{error}</p>}
          {saved && !dirty && <p className="text-[12px] text-emerald-700">Saved.</p>}
        </> : (
          <dl className="space-y-3 text-[12.5px]">
            <div><dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500">Label</dt><dd className="mt-0.5 text-ink-800">{item.displayLabel ?? '—'}</dd></div>
            <div><dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500">Caption</dt><dd className="mt-0.5 text-ink-800">{item.caption ?? '—'}</dd></div>
            <div><dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500">File</dt><dd className="mt-0.5 break-all text-ink-800">{item.fileName}</dd></div>
          </dl>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-ink-100 bg-ink-50/60 px-4 py-2.5">
        <Button variant="secondary" size="sm" onClick={() => void downloadItem(item).catch((e) => setError(errorText(e)))}><Download className="h-3.5 w-3.5" />Download</Button>
        {canUpdate && <Button size="sm" disabled={!dirty || busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save'}</Button>}
      </div>
    </div>
  );
}

export function MediaPreview({ items, index, onIndex, onClose, canUpdate, onSaved }: {
  items: MediaItem[]; index: number | null; onIndex: (i: number) => void; onClose: () => void; canUpdate: boolean; onSaved: (i: MediaItem) => void;
}) {
  const item = index === null ? undefined : items[index];
  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (!typing && e.key === 'ArrowLeft' && index > 0) onIndex(index - 1);
      else if (!typing && e.key === 'ArrowRight' && index < items.length - 1) onIndex(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, items.length, onClose, onIndex]);
  if (!item || index === null) return null;
  const nav = 'absolute top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-ink-900 shadow-pop transition hover:bg-white disabled:opacity-0';
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={`Preview of ${item.fileName}`} className="fixed inset-0 z-[120] flex flex-col bg-black/90 lg:flex-row">
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 items-center gap-3 px-4 py-3 text-[12.5px] text-white/80">
          <span className="tabular-nums">{index + 1} of {items.length}</span>
          <span className="min-w-0 flex-1 truncate">{item.fileName}</span>
          <button type="button" onClick={onClose} aria-label="Close preview" className="grid h-8 w-8 place-items-center rounded-full text-white/80 transition hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="relative flex min-h-0 flex-1 items-center justify-center px-14 pb-6">
          <Stage key={item.id} item={item} />
          <button type="button" aria-label="Previous" disabled={index === 0} onClick={() => onIndex(index - 1)} className={`${nav} left-3`}><ChevronLeft className="h-5 w-5" /></button>
          <button type="button" aria-label="Next" disabled={index === items.length - 1} onClick={() => onIndex(index + 1)} className={`${nav} right-3`}><ChevronRight className="h-5 w-5" /></button>
        </div>
      </div>
      <aside className="h-[42dvh] w-full shrink-0 border-t border-ink-200 bg-white lg:h-auto lg:w-[340px] lg:border-l lg:border-t-0">
        <Details key={item.id} item={item} canUpdate={canUpdate} onSaved={onSaved} />
      </aside>
    </div>,
    document.body,
  );
}
